/**
 * AuthContext — current Configural user + their organization context.
 *
 * - On mount, calls /api/auth/me. If a valid session cookie exists,
 *   populates user + org + accessible orgs.
 * - Exposes login, signup, logout helpers.
 * - `currentOrg` tracks which org the user is currently looking at.
 *   For platform_admin this can switch between any org (stored in
 *   localStorage). For org_users it is pinned to their single org.
 * - Every API request picks up the org slug via setCurrentOrgSlug()
 *   so the X-Organization header flows automatically.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import * as api from "./api.js";

const AuthContext = createContext(null);

const CURRENT_ORG_KEY = "configural.currentOrg";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [currentOrgSlug, setCurrentOrgSlugState] = useState(() => {
    try {
      return localStorage.getItem(CURRENT_ORG_KEY) || null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const applyOrgSlug = useCallback((slug) => {
    setCurrentOrgSlugState(slug);
    try {
      if (slug) localStorage.setItem(CURRENT_ORG_KEY, slug);
      else localStorage.removeItem(CURRENT_ORG_KEY);
    } catch {
      // ignore
    }
    api.setCurrentOrgSlug(slug);
  }, []);

  const refreshMe = useCallback(async () => {
    try {
      // make sure the api module's org slug matches whatever we had
      // from localStorage before issuing /auth/me
      api.setCurrentOrgSlug(currentOrgSlug);
      const res = await api.getMe();
      const nextUser = res.data.user;
      const nextOrgs = res.data.orgs || [];
      setUser(nextUser);
      setOrgs(nextOrgs);
      // Decide the current-org context for this user:
      //   - org_user:        always pinned to their own org
      //   - platform_admin:  honor the previously-saved slug IF they
      //                      still have access to it, otherwise null
      //                      ("platform mode" — no org picked yet).
      //                      We no longer auto-pick the first org on
      //                      login so the admin always starts in the
      //                      platform view.
      let resolved;
      if (nextUser.role === "platform_admin") {
        resolved =
          currentOrgSlug && nextOrgs.find((o) => o._id === currentOrgSlug)
            ? currentOrgSlug
            : null;
      } else {
        resolved = nextUser.org_id;
      }
      applyOrgSlug(resolved);
      setError(null);
      return nextUser;
    } catch (err) {
      // 401 means not logged in — that's fine, just clear state
      if (err?.status === 401) {
        setUser(null);
        setOrgs([]);
        applyOrgSlug(null);
        return null;
      }
      setError(err);
      return null;
    }
  }, [applyOrgSlug, currentOrgSlug]);

  useEffect(() => {
    (async () => {
      await refreshMe();
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = useCallback(
    async (email, password) => {
      const res = await api.login({ email, password });
      const u = res.data.user;
      const org = res.data.org;
      setUser(u);
      if (u.role === "platform_admin") {
        // Platform admin lands in "platform mode" — no org pre-selected.
        // They explicitly enter an org by clicking one in the admin
        // Organizations page or the top-bar switcher.
        const orgsRes = await api.listOrganizations();
        setOrgs(orgsRes.data || []);
        applyOrgSlug(null);
      } else {
        setOrgs(org ? [org] : []);
        applyOrgSlug(u.org_id);
      }
      return u;
    },
    [applyOrgSlug]
  );

  const signup = useCallback(
    async ({ email, password, name, organization_name, organization_slug, organization_description }) => {
      const res = await api.signup({
        email,
        password,
        name,
        organization_name,
        organization_slug,
        organization_description,
      });
      const u = res.data.user;
      const org = res.data.org;
      setUser(u);
      setOrgs([org]);
      applyOrgSlug(org._id);
      return u;
    },
    [applyOrgSlug]
  );

  const logout = useCallback(async () => {
    try {
      await api.logout();
    } catch {
      // ignore
    }
    setUser(null);
    setOrgs([]);
    applyOrgSlug(null);
  }, [applyOrgSlug]);

  const switchOrg = useCallback(
    (slug) => {
      if (user?.role !== "platform_admin") return;
      if (!orgs.find((o) => o._id === slug)) return;
      applyOrgSlug(slug);
    },
    [user, orgs, applyOrgSlug]
  );

  // Enter an org (platform admin picks one from the list / switcher).
  // Same as switchOrg but named for intent clarity at the call site.
  const enterOrg = switchOrg;

  // Exit to platform mode (clears the current org for platform admins).
  const exitOrg = useCallback(() => {
    if (user?.role !== "platform_admin") return;
    applyOrgSlug(null);
  }, [user, applyOrgSlug]);

  const currentOrg = useMemo(
    () => orgs.find((o) => o._id === currentOrgSlug) || null,
    [orgs, currentOrgSlug]
  );

  const value = {
    user,
    orgs,
    currentOrg,
    currentOrgSlug,
    loading,
    error,
    login,
    signup,
    logout,
    switchOrg,
    enterOrg,
    exitOrg,
    refreshMe,
    isPlatformAdmin: user?.role === "platform_admin",
    isOrgAdmin: !!user?.is_org_admin,
    // Convenience: true iff there is currently a selected org context.
    // In platform mode this is false.
    inOrgContext: !!currentOrgSlug,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
